import {
  useEffect,
  useState,
} from 'react';

type LocalAIStatus = {
  configured:
    boolean;

  operational:
    boolean;

  agents?:
    Array<{
      model?:
        string | null;

      online?:
        boolean;
    }>;
};

type AutentiqueStatus = {
  configured:
    boolean;

  connected:
    boolean;

  account?:
    {
      name?:
        string | null;

      email?:
        string | null;
    } |
    null;
};

type SystemHealthStatus = {
  status?: string;
  checks?: Array<{
    key?: string;
    label?: string;
    state?: string;
    lastError?: string | null;
  }>;
};

export function SystemHealthCard() {
  const [health, setHealth] = useState<SystemHealthStatus | null>(null);

  useEffect(() => {
    void fetch('/api/health', { credentials: 'same-origin' })
      .then(async (response) => (response.ok || response.status === 503 ? response.json() : null))
      .then((body: SystemHealthStatus | null) => {
        if (body) setHealth(body);
      })
      .catch(() => undefined);
  }, []);

  const state = String(health?.status ?? 'unknown').toUpperCase();
  const failedChecks = (health?.checks ?? []).filter(
    (check) => check?.state === 'FAILED' || check?.state === 'DEGRADED'
  );

  return (
    <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:col-span-2">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h4 className="text-sm font-semibold text-foreground-900">Saúde operacional</h4>
          <p className="text-xs text-foreground-500 mt-1">D1, Scheduler, IA local e integrações opcionais.</p>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
          state === 'HEALTHY'
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : state === 'DEGRADED'
              ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : 'bg-gray-100 text-gray-500'
        }`}>
          {state}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        {(health?.checks ?? []).slice(0, 8).map((check) => (
          <div key={check.key} className="rounded-lg border border-background-200 bg-white px-2.5 py-2">
            <div className="text-foreground-500 truncate">{check.label ?? check.key}</div>
            <div className="font-medium text-foreground-800">{check.state ?? 'UNKNOWN'}</div>
          </div>
        ))}
      </div>
      {failedChecks[0]?.lastError && (
        <p className="text-[11px] text-amber-700 mt-3">Atenção: {failedChecks[0].lastError}</p>
      )}
    </div>
  );
}

export function LocalAIIntegrationCard() {
  const [
    status,
    setStatus,
  ] =
    useState<
      LocalAIStatus |
      null
    >(null);

  useEffect(
    () => {
      void fetch(
        '/api/ai/status',
        {
          credentials:
            'same-origin',
        }
      )
        .then(
          async (
            response
          ) =>
            response.ok
              ? response.json()
              : null
        )
        .then(
          (
            body: any
          ) => {
            if (body) {
              setStatus({
                configured:
                  Boolean(
                    body.configured
                  ),

                operational:
                  Boolean(
                    body.operational
                  ),

                agents:
                  body.agents ??
                  [],
              });
            }
          }
        )
        .catch(
          () => undefined
        );
    },
    []
  );

  return (
    <div className="bg-background-50 border border-background-200 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
          <i className="ri-brain-line text-xl text-foreground-600"></i>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-foreground-900">
            IA local
          </h4>

          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              status?.operational
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}
          >
            {status?.operational
              ? 'Operacional'
              : status?.configured
                ? 'Agente offline'
                : 'Configuração necessária'}
          </span>
        </div>
      </div>

      <p className="text-xs text-foreground-500">
        Local Agent + Ollama. Processamento jurídico sem API comercial de IA.
      </p>

      {status?.agents?.[0]?.model && (
        <p className="text-[11px] text-foreground-500 mt-3">
          Modelo: {status.agents[0].model}
        </p>
      )}
    </div>
  );
}

export function SignatureIntegrationCard() {
  const [
    status,
    setStatus,
  ] =
    useState<
      AutentiqueStatus |
      null
    >(null);

  useEffect(
    () => {
      void fetch(
        '/api/signatures/status',
        {
          credentials:
            'same-origin',
        }
      )
        .then(
          async (
            response
          ) =>
            response.ok
              ? response.json()
              : null
        )
        .then(
          (
            body: any
          ) => {
            if (
              body?.autentique
            ) {
              setStatus(
                body.autentique
              );
            }
          }
        )
        .catch(
          () => undefined
        );
    },
    []
  );

  return (
    <div className="bg-background-50 border border-background-200 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
          <i className="ri-quill-pen-line text-xl text-foreground-600"></i>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-foreground-900">
            Autentique
          </h4>

          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              status?.connected
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}
          >
            {status?.connected
              ? 'Conectado'
              : status?.configured
                ? 'Credencial inválida'
                : 'Configuração necessária'}
          </span>
        </div>
      </div>

      <p className="text-xs text-foreground-500">
        PDFs finalizados, signatários, links de assinatura e sincronização de status.
      </p>

      {status?.connected && status?.account?.name && (
        <p className="text-[11px] text-foreground-500 mt-3">
          Conta: {status.account.name}
        </p>
      )}
    </div>
  );
}
